/**
 * Sign-in screen for the plain-browser case (never shown inside Teams): one email-or-username field,
 * a choice of password or emailed code, and Microsoft at the bottom. Without Entra configured the
 * card explains what to set instead of offering the Microsoft button. ⛔ Neither route calls its
 * sign-in directly: both go through `../lib/teams` (`signInWithMicrosoft`, `adoptOtpSession`), which
 * clear the other credential first, and `.tsx` handlers never run under test.
 */
import { useMutation } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import DemoSignIn from './DemoSignIn'
import ForgotPassword from './ForgotPassword'
import { PasswordInput } from './PasswordInput'
import { toast } from 'sonner'

import { api, errorMessage } from '../lib/api'
import { demoSignInEnabled } from '../lib/demoSignIn'
import {
  PASSWORD_SIGN_IN_PATH,
  PASSWORD_VERIFY_CODE_PATH,
  passwordChallenge,
  passwordText,
  secondStepFailure,
  signInFailure,
} from '../lib/password'
import { isMsalConfigured, missingMsalConfig, takeRedirectError } from '../lib/msal'
import { type SignInMethod, signInLayout } from '../lib/signInLayout'
import { adoptOtpSession, resumeMicrosoftSession, signInWithMicrosoft } from '../lib/teams'
import { useRequestOtpCode, useVerifyOtpCode } from '../lib/otp'
import { inputClass, labelClass, Notice } from './ui'
import { BRAND } from '../lib/brand'

const demoBuild = demoSignInEnabled(import.meta.env.VITE_DEMO_SIGNIN)

const primaryButton =
  'w-full rounded-lg bg-accent px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'

export default function SignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useTranslation()
  const [pending, setPending] = useState(false)

  useEffect(() => {
    const failure = takeRedirectError()
    if (failure) toast.error(`Microsoft sign-in did not complete: ${failure}`)
    void resumeMicrosoftSession().then((navigating) => {
      if (navigating) setPending(true)
    })
  }, [])

  async function onMicrosoftSignIn() {
    setPending(true)
    try {
      await signInWithMicrosoft()
      onSignedIn()
    } catch (error) {
      console.error('[signin] Microsoft sign-in failed:', error)
      toast.error(errorMessage(error))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4 sm:p-6 dark:bg-slate-900">
      <div className="w-full max-w-sm space-y-5 rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">{BRAND}</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('otp.signInPrompt')}</p>
        </div>

        {demoBuild ? (
          <DemoSignIn onSignedIn={onSignedIn} />
        ) : (
          <>
            <IdentifierSignIn onSignedIn={onSignedIn} />
            <OrDivider />
          </>
        )}

        {demoBuild ? null : isMsalConfigured ? (
          <>
            <button
              type="button"
              onClick={onMicrosoftSignIn}
              disabled={pending}
              className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/60"
            >
              <MicrosoftLogo />
              {pending ? t('otp.signInMicrosoftWaiting') : t('otp.signInMicrosoft')}
            </button>
            <p className="text-center text-xs text-slate-500 dark:text-slate-400">
              {t('otp.signInMicrosoftHint')}
            </p>
          </>
        ) : (
          <NotConfigured />
        )}
      </div>
    </div>
  )
}

function OrDivider() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-3 text-xs uppercase text-slate-500 dark:text-slate-400">
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
      {t('otp.orDivider')}
      <span className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
    </div>
  )
}

/** The 200s carry no token (the session arrives as the HttpOnly cookie) and `adoptOtpSession()` must still be awaited. */
function IdentifierSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const { t } = useTranslation()
  const [identifier, setIdentifier] = useState('')
  const [chosen, setChosen] = useState<SignInMethod>('password')
  const [password, setPassword] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [code, setCode] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [challenge, setChallenge] = useState<string | null>(null)
  const [forgot, setForgot] = useState(false)
  const [resetDone, setResetDone] = useState(false)
  const request = useRequestOtpCode()
  const verify = useVerifyOtpCode()

  const layout = signInLayout({ identifier, method: chosen, msalConfigured: isMsalConfigured })
  const email = identifier.trim()

  const signIn = useMutation({
    mutationFn: async () => (await api.post(PASSWORD_SIGN_IN_PATH, { handle: email, password })).data,
    onSuccess: async (data) => {
      const proof = passwordChallenge(data)
      if (proof) {
        setPassword('')
        setCode('')
        setChallenge(proof)
        return
      }
      try {
        await adoptOtpSession()
        onSignedIn()
      } catch (error) {
        console.error('[signin] could not take on the password session:', error)
        setFailure(errorMessage(error))
      }
    },
    onError: (error) => {
      setPassword('')
      const text = signInFailure(error)
      const why = secondStepFailure(error)
      if (text) setFailure(passwordText(t, text))
      else if (why === 'notSent') setFailure(t('otp.secondStepNotSent'))
      else if (why === 'tooMany') setFailure(t('otp.secondStepTooMany'))
      else setFailure(errorMessage(error))
    },
  })

  const secondStep = useMutation({
    mutationFn: async () => (await api.post(PASSWORD_VERIFY_CODE_PATH, { challenge, code })).data,
    onSuccess: async () => {
      try {
        await adoptOtpSession()
        onSignedIn()
      } catch (error) {
        console.error('[signin] could not take on the two-step session:', error)
        setFailure(errorMessage(error))
      }
    },
    onError: (error) => {
      setCode('')
      const text = signInFailure(error)
      if (text === 'companyDeactivated') setFailure(passwordText(t, text))
      else setFailure(text ? t('otp.secondStepRefused') : errorMessage(error))
    },
  })

  function startOver() {
    setChallenge(null)
    setCode('')
    setFailure(null)
    signIn.reset()
    secondStep.reset()
  }

  function choose(method: SignInMethod) {
    setChosen(method)
    setFailure(null)
    setCodeSent(false)
    setCode('')
    setNotice(null)
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    setFailure(null)
    if (challenge) {
      secondStep.mutate()
    } else if (layout.method === 'password') {
      signIn.mutate()
    } else if (!codeSent) {
      request.mutate(email, {
        onSuccess: (data) => {
          setNotice(data.detail)
          setCodeSent(true)
        },
        onError: (error) => toast.error(errorMessage(error)),
      })
    } else {
      verify.mutate(
        { email, code },
        {
          onSuccess: async () => {
            try {
              await adoptOtpSession()
              onSignedIn()
            } catch (error) {
              console.error('[signin] could not take on the code session:', error)
              toast.error(errorMessage(error))
            }
          },
          onError: (error) =>
            toast.error(
              signInFailure(error) === 'companyDeactivated'
                ? passwordText(t, 'companyDeactivated')
                : errorMessage(error),
            ),
        },
      )
    }
  }

  const passwordBusy = signIn.isPending || signIn.isSuccess
  const verifyBusy = verify.isPending || verify.isSuccess
  const secondStepBusy = secondStep.isPending || secondStep.isSuccess
  const options: { method: SignInMethod; label: string; disabled: boolean }[] = [
    { method: 'password', label: t('otp.methodPassword'), disabled: false },
    { method: 'code', label: t('otp.methodCode'), disabled: !layout.codeAvailable },
  ]

  if (forgot) {
    return (
      <ForgotPassword
        initialHandle={email}
        onBack={() => setForgot(false)}
        onReset={() => {
          setForgot(false)
          setPassword('')
          setFailure(null)
          setResetDone(true)
        }}
      />
    )
  }

  if (challenge) {
    return (
      <form onSubmit={submit} className="space-y-3">
        <p className="text-center text-sm text-slate-600 dark:text-slate-300">{t('otp.secondStepNotice')}</p>
        <label className="block text-sm">
          <span className={labelClass}>{t('otp.codeLabel')}</span>
          <input
            type="text"
            required
            autoFocus
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={`w-full ${inputClass}`}
          />
        </label>
        <button type="submit" disabled={secondStepBusy} className={primaryButton}>
          {secondStepBusy ? t('otp.verifying') : t('otp.verify')}
        </button>
        {failure && <Notice tone="danger">{failure}</Notice>}
        <button
          type="button"
          onClick={startOver}
          className="w-full text-center text-xs text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
        >
          {t('otp.startOver')}
        </button>
      </form>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <label className="block text-sm">
        <span className={labelClass}>{t('password.handleLabel')}</span>
        <input
          type="text"
          required
          autoFocus
          autoComplete="username"
          disabled={codeSent}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          className={`w-full ${inputClass}`}
        />
      </label>

      <div
        role="group"
        aria-label={t('otp.methodGroup')}
        className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-700/60"
      >
        {options.map((o) => (
          <button
            key={o.method}
            type="button"
            disabled={o.disabled}
            aria-pressed={layout.method === o.method}
            onClick={() => choose(o.method)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium focus-visible:outline-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-50 ${
              layout.method === o.method
                ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-800 dark:text-slate-100'
                : 'text-slate-600 hover:text-slate-800 dark:text-slate-300 dark:hover:text-slate-100'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {!layout.codeAvailable && identifier.trim() !== '' && (
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('otp.codeNeedsEmail')}</p>
      )}

      {layout.method === 'password' && (
        <>
          <PasswordInput
            label={t('password.passwordLabel')}
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button type="submit" disabled={passwordBusy} className={primaryButton}>
            {passwordBusy ? t('password.signingIn') : t('password.signIn')}
          </button>
          {resetDone && !failure && <Notice tone="info">{t('otp.forgotDone')}</Notice>}
          {failure && <Notice tone="danger">{failure}</Notice>}
          <button
            type="button"
            onClick={() => setForgot(true)}
            className="w-full text-center text-xs text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
          >
            {t('otp.forgotLink')}
          </button>
        </>
      )}

      {layout.method === 'code' && !codeSent && (
        <button type="submit" disabled={request.isPending} className={primaryButton}>
          {request.isPending ? t('otp.sending') : t('otp.sendCode')}
        </button>
      )}

      {layout.method === 'code' && codeSent && (
        <>
          <p className="text-center text-sm text-slate-600 dark:text-slate-300">{notice}</p>
          <label className="block text-sm">
            <span className={labelClass}>{t('otp.codeLabel')}</span>
            <input
              type="text"
              required
              autoFocus
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`w-full ${inputClass}`}
            />
          </label>
          <button type="submit" disabled={verifyBusy} className={primaryButton}>
            {verifyBusy ? t('otp.verifying') : t('otp.verify')}
          </button>
          <button
            type="button"
            onClick={() => choose('code')}
            className="w-full text-center text-xs text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300"
          >
            {t('otp.changeEmail')}
          </button>
        </>
      )}
    </form>
  )
}

function NotConfigured() {
  const missing = missingMsalConfig()
  return (
    <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
      <p className="font-medium">Microsoft sign-in is not configured.</p>
      <p>
        Create <code className="rounded bg-amber-100 px-1 dark:bg-amber-900/50">frontend/.env.local</code> with these
        values from your Entra app registration, then restart the dev server —
        Vite only reads env files at startup:
      </p>
      <ul className="ml-4 list-disc font-mono text-xs">
        {missing.map((name) => (
          <li key={name}>{name}</li>
        ))}
      </ul>
    </div>
  )
}

/** Inline so there is no external asset. */
function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}
